import * as React from 'react';

import { useEventCallback } from './useEventCallback';

/**
 * Call `onActive` whenever the page transitions back to the foreground: when the
 * tab becomes visible again or the window regains focus after being in the
 * background.
 *
 * The callback fires only on the inactive → active *transition*, not on every
 * focus/visibility event — so moving focus around within an already-active page
 * does not trigger it. "Active" means the document is visible and the window has
 * focus.
 *
 * Typically used to wrap a WebSocket-like connection that reconnects with a
 * backoff: after the page has been hidden for a while (computer asleep, tab
 * backgrounded), the socket is closed and we might be deep into that backoff.
 * Reconnecting as soon as the user comes back avoids leaving them on a
 * disconnected state waiting for the next scheduled attempt.
 *
 * Passing `undefined` disables the hook entirely: no listeners are attached and
 * nothing happens when the page becomes active.
 */
export function useOnPageBecomeActive(onActive: (() => void) | undefined): void {
    const onPageActive = useEventCallback(onActive ?? (() => {}));
    const enabled = onActive !== undefined;

    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        const isPageActive = () => document.visibilityState === 'visible' && document.hasFocus();

        let wasActive = isPageActive();

        const handleActivityChange = () => {
            const active = isPageActive();
            // Only react to the transition into active, ignoring events that fire while
            // the page is already (or still) in the foreground.
            if (active && !wasActive) {
                onPageActive();
            }
            wasActive = active;
        };

        document.addEventListener('visibilitychange', handleActivityChange);
        window.addEventListener('focus', handleActivityChange);
        window.addEventListener('blur', handleActivityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleActivityChange);
            window.removeEventListener('focus', handleActivityChange);
            window.removeEventListener('blur', handleActivityChange);
        };
    }, [onPageActive, enabled]);
}
